import { format, isSameYear, isToday, isValid, parseISO } from 'date-fns';
import i18n, { t } from 'i18next';

import { getDateFnsCurrentLocale } from '../i18n/I18n';

export function isValidDate(date) {
    return date instanceof Date && isValid(date);
}

export function formatDateTime(dateTime) {
    if (!dateTime) {
        return '';
    }
    const dateTimeObject = isValidDate(dateTime) ? dateTime : parseISO(dateTime);
    return format(dateTimeObject, `PP '${t('at')}' p`, {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeShort(dateTime) {
    if (!dateTime) {
        return '';
    }
    const dateTimeObject = isValidDate(dateTime) ? dateTime : parseISO(dateTime);
    return format(dateTimeObject, 'Pp', { locale: getDateFnsCurrentLocale(i18n.language) });
}

export function formatDate(dateTime) {
    if (!dateTime) {
        return '';
    }
    const dateTimeObject = isValidDate(dateTime) ? dateTime : parseISO(dateTime);

    return format(dateTimeObject, 'PP', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatTime(dateTime) {
    if (!dateTime) {
        return '';
    }
    const dateTimeObject = isValidDate(dateTime) ? dateTime : parseISO(dateTime);

    return format(dateTimeObject, 'p', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeFromRange(range, date) {
    return range && date ? formatDateTime(date) : '';
}

export function formatDateTimeRange(start, end) {
    const startDate = isValidDate(start) ? new Date(start) : null;
    const endDate = isValidDate(end) ? new Date(end) : null;
    if (!start) return '-';

    if (!endDate) return formatDateTime(startDate);

    if (startDate.toDateString() === endDate.toDateString()) {
        // Show date once and time range
        const locale = getDateFnsCurrentLocale(i18n.language);
        return format(startDate, 'PPp', { locale }).replace(
            format(startDate, 'p', { locale }),
            `${format(startDate, 'p', { locale })} - ${format(endDate, 'p', { locale })}`
        );
    }

    return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
}

export function formatRelativeDate(date) {
    if (isToday(date)) {
        return format(date, "HH'h'mm");
    }
    if (isSameYear(date, new Date())) {
        return format(date, 'dd MMM');
    }
    return formatDate(date, 'dd/MM/yy');
}

export function formatDateTimeRangeForOrder(datetimeRange, formatter = formatDateTime) {
    if (!datetimeRange) {
        return t('Not specified');
    }
    const from = datetimeRange?.start?.value || datetimeRange?.start;
    const to = datetimeRange?.end?.value || datetimeRange?.end;
    const startDatetime = from ? new Date(datetimeRange?.start?.value || datetimeRange?.start) : null;
    const endDatetime = to ? new Date(datetimeRange?.end?.value || datetimeRange?.end) : null;

    if (startDatetime && format(startDatetime, 'HH:mm') === '08:00' && format(endDatetime, 'HH:mm') === '12:00') {
        return `${formatDate(startDatetime)} - ${t('Morning')}`;
    }
    if (endDatetime && format(startDatetime, 'HH:mm') === '14:00' && format(endDatetime, 'HH:mm') === '18:00') {
        return `${formatDate(startDatetime)} - ${t('Afternoon')}`;
    }
    return formatter(startDatetime);
}

export function formatDateToLocaleForExport(dateToFormat, i18nLanguage) {
    return format(new Date(dateToFormat), 'yyyy-MM-dd HH:mm:ss', {
        locale: getDateFnsCurrentLocale(i18nLanguage),
    });
}
